import moment from "moment";

import { TFunction } from "utils";

import { CROP_TYPE_FCPO, CarbonProgram, CropFCPO, FieldPracticeResponse, INCENTIVE_NAME, PRACTICE_SLUG, PracticeFCPO, ZERO_UUID } from "models";

import { PracticeAssessmentData } from "components";

import { getCropYearFromDates } from "pages/app/all-field-view/utils/AllFieldsUtils";

const roundHa = (v: number) => Math.round(v);

const getPracticeYear = (practice: PracticeFCPO, crops: CropFCPO[]): number | null => {
  if (practice.cropId) {
    const crop = crops.find((c) => c.id === practice.cropId);
    if (!crop) return null;
    return getCropYearFromDates(crop.cropType, moment(crop.seedingStartDate), moment(crop.harvestEndDate));
  }
  return practice.year ?? null;
};

// Conservation tillage mapping mirrors what getInputRequirementConfig uses to detect the
// incentive: carbonProgram.fieldsManagement.practices[slug=tillages].incentives[name=conservation_tillage].
// The incentive's elementIds (when configured) hold the practice IDs that qualify.
const getConservationTillageElementIds = (carbonProgram: CarbonProgram | null | undefined): string[] | null => {
  if (!carbonProgram?.fieldsManagement.practices) return null;
  const tillagePractice = carbonProgram.fieldsManagement.practices.find((p) => p.slug === PRACTICE_SLUG.TILLAGES);
  const conservationIncentive = tillagePractice?.incentives?.find((i) => i.name === INCENTIVE_NAME.conservationTillage);
  return conservationIncentive?.elementIds ?? null;
};

const fieldHasConservationTillage = (fieldData: FieldPracticeResponse, reportingYear: number, conservationTillageElementIds: string[] | null): boolean => {
  const tillages = fieldData.practices.filter(
    (p) => p.type === PRACTICE_SLUG.TILLAGES && p.assignedPracticeId !== ZERO_UUID && getPracticeYear(p, fieldData.crops) === reportingYear
  );
  if (!tillages.length) return false;
  // When no elementIds are configured for the program, count any non-placeholder tillage as
  // conservation tillage — this matches how the inventory panel implicitly behaves (it trusts
  // the server-computed earnedIncentives rather than re-applying a depth threshold).
  if (!conservationTillageElementIds?.length) return true;
  return tillages.some((t) => conservationTillageElementIds.includes(t.assignedPracticeId));
};

export const buildPracticeAssessmentData = (
  allFieldsPractices: Record<string, FieldPracticeResponse>,
  fieldSizes: Record<string, number>,
  reportingYear: number,
  carbonProgram: CarbonProgram | null | undefined,
  t: TFunction
): PracticeAssessmentData | null => {
  const entries = Object.entries(allFieldsPractices);
  if (!entries.length) return null;

  const conservationTillageElementIds = getConservationTillageElementIds(carbonProgram);

  let coverCropsActualHa = 0;
  let undersowingActualHa = 0;
  let residueActualHa = 0;
  let conservationTillageActualHa = 0;
  let organicFertilizerActualHa = 0;

  let totalEnrolledHa = 0;
  let fieldsWithAnyPracticeHa = 0;
  let missingFieldsCount = 0;
  let totalFieldsCount = 0;

  entries.forEach(([fieldId, fieldData]) => {
    const ha = fieldSizes[fieldId] ?? 0;

    totalEnrolledHa += ha;

    // Actual ha: reporting year only, requires a MAIN crop
    const mainCrop = fieldData.crops.find(
      (c) => c.cropType === CROP_TYPE_FCPO.MAIN && getCropYearFromDates(c.cropType, moment(c.seedingStartDate), moment(c.harvestEndDate)) === reportingYear
    );

    totalFieldsCount += 1;

    const hasCoverCrop = fieldData.crops.some(
      (c) => c.cropType === CROP_TYPE_FCPO.COVER && getCropYearFromDates(c.cropType, moment(c.seedingStartDate), moment(c.harvestEndDate)) === reportingYear
    );
    if (hasCoverCrop) coverCropsActualHa += ha;

    const hasUndersowing = fieldData.crops.some(
      (c) =>
        c.cropType === CROP_TYPE_FCPO.UNDERSOWING && getCropYearFromDates(c.cropType, moment(c.seedingStartDate), moment(c.harvestEndDate)) === reportingYear
    );
    if (hasUndersowing) undersowingActualHa += ha;

    const hasResidue = mainCrop?.hasResidue === true;
    if (hasResidue) residueActualHa += ha;

    const hasConservationTillage = fieldHasConservationTillage(fieldData, reportingYear, conservationTillageElementIds);
    if (hasConservationTillage) conservationTillageActualHa += ha;

    const hasOrganicFertilizer = fieldData.practices.some(
      (p) => p.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && p.assignedPracticeId !== ZERO_UUID && getPracticeYear(p, fieldData.crops) === reportingYear
    );
    if (hasOrganicFertilizer) organicFertilizerActualHa += ha;

    const hasAnyPractice = hasCoverCrop || hasUndersowing || hasResidue || hasConservationTillage || hasOrganicFertilizer;
    if (hasAnyPractice) fieldsWithAnyPracticeHa += ha;

    // A field is "missing data" when no practice is reported AND residue is unknown
    const residueUnknown = !mainCrop || mainCrop.hasResidue === null;
    if (!hasAnyPractice && residueUnknown) missingFieldsCount += 1;
  });

  if (!totalFieldsCount) return null;

  return {
    year: reportingYear,
    totalHa: roundHa(totalEnrolledHa),
    totalActualHa: roundHa(fieldsWithAnyPracticeHa),
    practices: [
      {
        key: "cover_crops",
        label: t("impactPanel.practiceAssessment.practices.coverCrops"),
        actual: coverCropsActualHa > 0 ? roundHa(coverCropsActualHa) : null,
        target: roundHa(totalEnrolledHa),
      },
      {
        key: "undersowing",
        label: t("impactPanel.practiceAssessment.practices.undersowing"),
        actual: undersowingActualHa > 0 ? roundHa(undersowingActualHa) : null,
        target: roundHa(totalEnrolledHa),
      },
      {
        key: "residue",
        label: t("impactPanel.practiceAssessment.practices.residue"),
        actual: residueActualHa > 0 ? roundHa(residueActualHa) : null,
        target: roundHa(totalEnrolledHa),
      },
      {
        key: "conservation_tillage",
        label: t("impactPanel.practiceAssessment.practices.conservationTillage"),
        actual: conservationTillageActualHa > 0 ? roundHa(conservationTillageActualHa) : null,
        target: roundHa(totalEnrolledHa),
      },
      {
        key: "organic_fertiliser",
        label: t("impactPanel.practiceAssessment.practices.organicFertiliser"),
        actual: organicFertilizerActualHa > 0 ? roundHa(organicFertilizerActualHa) : null,
        target: roundHa(totalEnrolledHa),
      },
    ],
    missingFieldsCount,
    totalFieldsCount,
  };
};
